// Ansicht Standort (Issue #491/#494/#495).
//
// Die erste der vier Ansichten: **wo** die Anlage steht. Danach kommt das Anlagenschema (was
// steht da), der Signalgraph (wie wird gerechnet) und der Monitor (was passiert gerade).
//
// Die Ansicht ist zugleich der Verortungs-Editor. Automatik verortet nie alles richtig, deshalb
// muss jede Marke mit der Maus zu setzen, zu verschieben und wieder zu lösen sein. Gespeichert
// wird über PUT /schematic — dieselbe Historie, dasselbe Audit, derselbe Rollback wie beim
// Anlagenschema, weil die Verortung Teil des Schemas ist und kein zweiter Speicher.

import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import { putSchematic } from '../api/client';
import type { LonLat, PlantSchematic, SchematicBody } from '../api/types';
import { Button, EmptyState, LabeledInput, LabeledSelect, PanelHeading } from '../components/ui';
import { useI18n } from '../i18n';
import { useMonitorRun } from '../state/useMonitorRun';
import { useStore } from '../state/store';
import type { Messwert } from '../site/StandortKarte';
import {
  istVerortet,
  karteMoeglich,
  loescheUmriss,
  loeseGeo,
  setzeGeo,
  setzeKoerper,
  setzeTrasse,
  setzeUmriss,
  trassenlaengeM,
  verortungsStand,
  verschiebeGeo,
} from '../site/verortung';

// Die Karte zieht CesiumJS nach — erst beim Öffnen dieser Ansicht, nie beim Start der IDE.
const StandortKarte = lazy(() => import('../site/StandortKarte'));

/** Voreinstellung für Nordrhein-Westfalen: offenes Luftbild, 10 cm, dl-de/zero-2.0. */
const KULISSE_NRW = {
  art: 'wms' as const,
  url: 'https://www.wms.nrw.de/geobasis/wms_nw_dop',
  layer: 'nw_dop_rgb',
  attribution: 'Luftbild: Geobasis NRW, dl-de/zero-2.0',
};

type Werkzeug = 'setzen' | 'umriss' | 'trasse' | null;

export function SiteView(): JSX.Element {
  const { t } = useI18n();
  const { activeProjectId, schematic, setSchematic, catalog, notify, reportError } = useStore();
  const { latest } = useMonitorRun();

  const [auswahl, setAuswahl] = useState<string | null>(null);
  const [bearbeiten, setBearbeiten] = useState(false);
  const [werkzeug, setWerkzeug] = useState<Werkzeug>(null);
  const [punkte, setPunkte] = useState<LonLat[]>([]);
  const [offen, setOffen] = useState(false); // ungespeicherte Änderungen
  const [kartenFehler, setKartenFehler] = useState<Error | null>(null);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    setAuswahl(null);
    setWerkzeug(null);
    setPunkte([]);
    setOffen(false);
  }, [activeProjectId]);

  // ── Messwerte je Knoten: über source.symbol des Signalkatalogs ─────────────────────
  const werte = useMemo(() => {
    const je = new Map<string, Messwert>();
    for (const v of catalog?.variables ?? []) {
      const knoten = v.source?.symbol;
      if (!knoten || je.has(knoten)) continue; // der erste Eingang je Knoten färbt ein
      const tick = latest.get(v.tag);
      if (!tick || typeof tick.value !== 'number') continue;
      je.set(knoten, { value: tick.value, min: v.range?.min, max: v.range?.max });
    }
    return je;
  }, [catalog, latest]);

  const einheitJeKnoten = useMemo(() => {
    const je = new Map<string, string>();
    for (const v of catalog?.variables ?? []) {
      const knoten = v.source?.symbol;
      if (knoten && !je.has(knoten)) je.set(knoten, v.unit ?? '');
    }
    return je;
  }, [catalog]);

  const aendere = useCallback(
    (naechstes: PlantSchematic) => {
      setSchematic(naechstes);
      setOffen(true);
    },
    [setSchematic],
  );

  // ── Klick in die Karte: hängt am gewählten Werkzeug ────────────────────────────────
  const aufBodenKlick = useCallback(
    (lat: number, lon: number) => {
      if (!schematic) return;
      if (werkzeug === 'setzen' && auswahl) {
        const knoten = schematic.nodes.find((n) => n.id === auswahl);
        aendere(
          knoten && istVerortet(knoten)
            ? verschiebeGeo(schematic, auswahl, lat, lon)
            : setzeGeo(schematic, auswahl, { lat, lon, koerper: 'zylinder', groesse_m: 12, hoehe_m: 4 }),
        );
        return;
      }
      if (werkzeug === 'umriss' || werkzeug === 'trasse') setPunkte((p) => [...p, [lon, lat]]);
    },
    [schematic, werkzeug, auswahl, aendere],
  );

  const zeichnungAbschliessen = useCallback(() => {
    if (!schematic || !auswahl) return;
    if (werkzeug === 'umriss' && punkte.length >= 3) aendere(setzeUmriss(schematic, auswahl, punkte));
    if (werkzeug === 'trasse' && punkte.length >= 2) aendere(setzeTrasse(schematic, auswahl, punkte));
    setPunkte([]);
    setWerkzeug(null);
  }, [schematic, auswahl, werkzeug, punkte, aendere]);

  const speichern = useCallback(async () => {
    if (!activeProjectId || !schematic) return;
    setSpeichert(true);
    try {
      await putSchematic(activeProjectId, schematic);
      setOffen(false);
      notify('success', t('site.gespeichert'));
    } catch (fehler) {
      reportError(fehler, t('site.speichernFehler'));
    } finally {
      setSpeichert(false);
    }
  }, [activeProjectId, schematic, notify, reportError, t]);

  const standortAnlegen = useCallback(() => {
    if (!schematic) return;
    // Ohne verortete Knoten gibt es noch keinen sinnvollen Mittelpunkt — der Editor beginnt dann
    // an der gewählten Vorgabe und wird von Hand zurechtgerückt.
    const mitte = schematic.nodes.find(istVerortet)?.geo;
    aendere({
      ...schematic,
      site: {
        name: schematic.project,
        mittelpunkt: mitte ? { lat: mitte.lat, lon: mitte.lon } : { lat: 51.2, lon: 7.1 },
        kamera: { hoehe_m: 450, neigung_grad: -35, richtung_grad: 0 },
        kulisse: KULISSE_NRW,
      },
    });
    setBearbeiten(true);
  }, [schematic, aendere]);

  if (!activeProjectId || !schematic) {
    return <EmptyState title={t('site.keinProjekt')} hint={t('site.keinProjektHinweis')} />;
  }

  const stand = verortungsStand(schematic);
  const gewaehlt = schematic.nodes.find((n) => n.id === auswahl) ?? null;
  const kanZeigen = karteMoeglich(schematic);

  return (
    <div className="flex h-full min-h-0">
      {/* ── Seitenleiste: Stand, Werkzeuge, Knotenliste ─────────────────────────────── */}
      <aside className="flex w-80 shrink-0 flex-col border-r border-bf-border bg-bf-navy">
        <div className="border-b border-bf-border px-4 py-3">
          <PanelHeading>{schematic.site?.name ?? t('nav.site')}</PanelHeading>
          <p className="mt-1 text-xs text-bf-muted" data-testid="site-stand">
            {t('site.stand', { verortet: stand.verortet, gesamt: stand.gesamt })}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              variant={bearbeiten ? 'primary' : 'secondary'}
              onClick={() => {
                setBearbeiten((b) => !b);
                setWerkzeug(null);
                setPunkte([]);
              }}
            >
              {bearbeiten ? t('site.bearbeitenAus') : t('site.bearbeitenAn')}
            </Button>
            {offen && (
              <Button onClick={speichern} disabled={speichert}>
                {t('site.speichern')}
              </Button>
            )}
          </div>
        </div>

        {bearbeiten && (
          <div className="border-b border-bf-border px-4 py-3 text-xs" data-testid="site-werkzeuge">
            <p className="mb-2 text-bf-muted">
              {auswahl ? t('site.werkzeugHinweis') : t('site.erstWaehlen')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={werkzeug === 'setzen' ? 'primary' : 'secondary'}
                disabled={!auswahl}
                onClick={() => {
                  setWerkzeug(werkzeug === 'setzen' ? null : 'setzen');
                  setPunkte([]);
                }}
              >
                {t('site.werkzeugSetzen')}
              </Button>
              <Button
                variant={werkzeug === 'umriss' ? 'primary' : 'secondary'}
                disabled={!auswahl}
                onClick={() => {
                  setWerkzeug(werkzeug === 'umriss' ? null : 'umriss');
                  setPunkte([]);
                }}
              >
                {t('site.werkzeugUmriss')}
              </Button>
              <Button
                variant={werkzeug === 'trasse' ? 'primary' : 'secondary'}
                disabled={!auswahl}
                onClick={() => {
                  setWerkzeug(werkzeug === 'trasse' ? null : 'trasse');
                  setPunkte([]);
                }}
              >
                {t('site.werkzeugTrasse')}
              </Button>
            </div>

            {(werkzeug === 'umriss' || werkzeug === 'trasse') && (
              <div className="mt-2 flex items-center gap-2" data-testid="site-zeichnung">
                <span className="text-bf-muted">
                  {t('site.punkteGesetzt', { anzahl: punkte.length })}
                  {werkzeug === 'trasse' && punkte.length >= 2
                    ? ` · ${Math.round(trassenlaengeM(punkte))} m`
                    : ''}
                </span>
                <Button onClick={zeichnungAbschliessen}>{t('site.zeichnungFertig')}</Button>
                <Button variant="secondary" onClick={() => setPunkte([])}>
                  {t('common.cancel')}
                </Button>
              </div>
            )}

            {gewaehlt?.geo && (
              <div className="mt-3 space-y-2" data-testid="site-koerper">
                <LabeledSelect
                  label={t('site.koerper')}
                  value={gewaehlt.geo.koerper ?? 'zylinder'}
                  onChange={(wert) =>
                    aendere(setzeKoerper(schematic, gewaehlt.id, { koerper: wert as SchematicBody }))
                  }
                >
                  <option value="zylinder">{t('site.koerperZylinder')}</option>
                  <option value="quader">{t('site.koerperQuader')}</option>
                  <option value="marke">{t('site.koerperMarke')}</option>
                </LabeledSelect>
                <LabeledInput
                  label={t('site.hoehe')}
                  type="number"
                  value={String(gewaehlt.geo.hoehe_m ?? 4)}
                  onChange={(wert) =>
                    aendere(setzeKoerper(schematic, gewaehlt.id, { hoehe_m: Number(wert) }))
                  }
                />
                <LabeledInput
                  label={t('site.groesse')}
                  type="number"
                  value={String(gewaehlt.geo.groesse_m ?? 12)}
                  onChange={(wert) =>
                    aendere(setzeKoerper(schematic, gewaehlt.id, { groesse_m: Number(wert) }))
                  }
                />
                <div className="flex gap-2">
                  {gewaehlt.geo.umriss && (
                    <Button
                      variant="secondary"
                      onClick={() => aendere(loescheUmriss(schematic, gewaehlt.id))}
                    >
                      {t('site.umrissLoeschen')}
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => aendere(loeseGeo(schematic, gewaehlt.id))}
                  >
                    {t('site.verortungLoesen')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto" data-testid="site-liste">
          {schematic.nodes.map((node) => {
            const wert = werte.get(node.id);
            const verortet = istVerortet(node);
            return (
              <button
                key={node.id}
                type="button"
                data-testid={`site-knoten-${node.id}`}
                onClick={() => setAuswahl(node.id)}
                className={`flex w-full items-center gap-2 border-b border-bf-border/60 px-4 py-1.5 text-left text-xs hover:bg-bf-navy-light ${
                  auswahl === node.id ? 'bg-bf-navy-light' : ''
                }`}
              >
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-sm ${
                    verortet ? 'bg-bf-gold' : 'border border-bf-muted bg-transparent'
                  }`}
                />
                <span className="flex-1 truncate text-bf-white">{node.label ?? node.id}</span>
                {wert ? (
                  <span className="tabular-nums text-bf-gold">
                    {wert.value.toFixed(2)}
                    <span className="ml-1 text-bf-muted">{einheitJeKnoten.get(node.id)}</span>
                  </span>
                ) : (
                  <span className="text-bf-muted">{verortet ? '—' : t('site.offen')}</span>
                )}
              </button>
            );
          })}
        </div>

        {schematic.site?.kulisse?.attribution && (
          <p className="border-t border-bf-border px-4 py-2 text-[11px] text-bf-muted">
            {schematic.site.kulisse.attribution}
          </p>
        )}
      </aside>

      {/* ── Karte ───────────────────────────────────────────────────────────────────── */}
      <div className="relative min-w-0 flex-1">
        {!kanZeigen ? (
          <EmptyState
            title={t('site.keinStandort')}
            hint={t('site.keinStandortHinweis')}
            action={<Button onClick={standortAnlegen}>{t('site.standortAnlegen')}</Button>}
          />
        ) : kartenFehler ? (
          <EmptyState title={t('site.karteFehlt')} hint={kartenFehler.message} />
        ) : (
          <Suspense fallback={<EmptyState title={t('common.loading')} />}>
            <StandortKarte
              schematic={schematic}
              werte={werte}
              auswahl={auswahl}
              aufWaehlen={setAuswahl}
              bearbeiten={bearbeiten && werkzeug !== null}
              aufBodenKlick={aufBodenKlick}
              aufFehler={setKartenFehler}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}
